import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { EmptySection } from '../../components/EmptySection';
import {
  Button,
  Stack,
  styled,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useTheme,
} from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo } from 'react';
import {
  Controller,
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { useAtomValue } from 'jotai';
import { useNavigate, useParams } from 'react-router';
import { Loader } from '../../components/Loader/Loader';
import { kpiActiveTemplatesListStateLoadableJ } from '../../services/state/KPI/KPITemplatesStateJ';
import { PageContainer } from '../../components/Layout/PageContainer';
import { ROUTES } from '../../constants/RouteNameMapping';
import { createKPITemplateDataModel, IKPITemplate } from '../../data-models/kpi-template.data-model';
import {
  ClientSettingsPreferences,
  PreferenceKey,
  PreferenceScope,
} from '../../data-models/preferences-v3.data-model';
import { kpiTemplatesListStateLoadableJ } from '../../services/state/KPI/KPITemplatesStateJ';
import { colors } from '../../theme/colors';
import { MaggieFeatureFlags } from '../../util/feature-flags';
import { PreferencesServiceV3 } from '../../util/preferences-service-v3';
import { KPITemplateFormBuilder } from './components/KPITemplateFormBuilder';
import { useKPITemplateActions } from './hooks/useKPITemplateActions';
import { KpiTemplateFormNavBlocker } from './KpiTemplateFormNavBlocker';
import { assignSortOrderTemplateSections, sortTemplateSections } from './utils';

const TemplateNameTextField = styled(TextField)({
  '& label.Mui-focused': {
    color: 'transparent',
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: 'transparent',
  },
  '& .MuiOutlinedInput-root': {
    paddingLeft: '0',
    '& fieldset': {
      borderColor: 'transparent',
    },
    '&:hover fieldset': {
      borderColor: 'transparent',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'transparent',
    },
  },
});

export function KPITemplate() {
  const selectedTemplate = useCurrentTemplate();

  const { colors } = useTheme();
  const methods = useForm<IKPITemplate>({
    mode: 'onTouched',
    values: selectedTemplate,
  });
  const {
    formState: { errors },
  } = methods;

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <Stack direction='row' justifyItems={'space-between'} alignItems={'center'} gap={'1rem'}>
          <Controller
            name='name'
            control={methods.control}
            defaultValue={selectedTemplate?.name || ''}
            render={({ field: { ref, ...field } }) => (
              <TemplateNameTextField
                {...field}
                required
                fullWidth
                sx={{
                  '& fieldset': { border: 'none' },
                  marginLeft: '-1rem',
                }}
                placeholder='Template Name'
                error={!!errors.name}
                helperText={errors.name ? <>{errors.name?.message}</> : ''}
                inputRef={ref}
                slotProps={{
                  htmlInput: {
                    style: {
                      fontSize: '1.5rem',
                      fontWeight: 300,
                      color: colors.primary[60],
                    },
                    autoFocus: true,
                  },
                }}
              />
            )}
          />
          <IsManagedServiceField />
          {selectedTemplate?.id && <ActionBar template={selectedTemplate} />}
        </Stack>
        <Stack direction='row' gap={'.1rem'} width={'100%'} mt={'1.5rem'}>
          <KPITemplateFormBuilder />
        </Stack>
      </FormProvider>
    </PageContainer>
  );
}

interface IActionBarProps {
  template: IKPITemplate;
}
function ActionBar(props: IActionBarProps) {
  const { formState, watch, handleSubmit, trigger } = useFormContext();
  const { isDirty, isValid, isSubmitting } = formState;
  const name = watch('name').trim();
  const disableSave = useMemo(() => {
    return (!isDirty && isValid && !isSubmitting) || !name;
  }, [isDirty, isSubmitting, isValid, name]);
  const navigate = useNavigate();
  const { updateKPITemplateAction: updateKPITemplate, createKPITemplateAction } = useKPITemplateActions();

  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    async (data: FieldValues) => {
      const dataWithSortedSections = assignSortOrderTemplateSections(data);

      if (props.template.id === -1) {
        const kpiTemplate = await createKPITemplateAction(dataWithSortedSections);

        if (!kpiTemplate?.id) {
          return;
        }

        return navigate(`/${ROUTES.KPI_TEMPLATES_FULL_ROUTE}/${kpiTemplate.id}`, {
          state: { isProgrammatic: true },
        });
      }
      await updateKPITemplate(dataWithSortedSections);
    },
    [createKPITemplateAction, navigate, props.template.id, updateKPITemplate]
  );

  return (
    <>
      <Stack direction='row' gap={'.5rem'}>
        <Button
          onClick={handleSubmit(onSubmit)}
          color='secondary'
          variant='contained'
          disabled={disableSave}
          size='medium'
          sx={{ width: 'max-content' }}
        >
          Save Changes
        </Button>
      </Stack>

      <KpiTemplateFormNavBlocker onSave={handleSubmit(onSubmit)} trigger={trigger} isDirty={isDirty} />
    </>
  );
}

function useCurrentTemplate() {
  const { templateId } = useParams();
  const isNewTemplate = useMemo(() => templateId === 'new', [templateId]);
  const templateListLoadable = useAtomValue(kpiTemplatesListStateLoadableJ);
  const templateList = templateListLoadable.state === 'hasData' ? templateListLoadable.data : undefined;

  return useMemo(() => {
    if (!templateList || isNewTemplate) {
      return createKPITemplateDataModel({
        id: -1,
        name: '',
        sections: [],
      });
    }

    const selectedTemplated = templateList.find((template) => `${template.id}` === templateId)!;

    return sortTemplateSections(selectedTemplated) as IKPITemplate;
  }, [isNewTemplate, templateId, templateList]);
}

const managedServiceInfo =
  'Default templates require VC approval only. The managed services templates also permit the dedicated team to accept or reject KPI requests submitted by PortCos.';
function IsManagedServiceField() {
  const methods = useFormContext();
  const { useManagedServices } = useFlags<MaggieFeatureFlags>();
  const clientUsesManagedServices = PreferencesServiceV3.get().getPreference<ClientSettingsPreferences>(
    PreferenceKey.clientSettings,
    PreferenceScope.ORGANIZATION
  )?.managedServices;

  if (!useManagedServices || !clientUsesManagedServices) {
    return null;
  }

  return (
    <Stack direction='row' gap={'.5rem'} justifyContent={'flex-end'}>
      <Controller
        name='isManagedService'
        control={methods.control}
        render={({ field }) => {
          return (
            <ToggleButtonGroup
              color='secondary'
              size='small'
              exclusive
              value={field.value ?? false}
              onChange={(e, newValue: boolean | null) => {
                if (newValue == null) {
                  return;
                }
                field.onChange(newValue);
              }}
            >
              <ToggleButton value={false}>Default</ToggleButton>
              <ToggleButton value={true} sx={{ width: 'max-content' }}>
                Managed Service
              </ToggleButton>
            </ToggleButtonGroup>
          );
        }}
      />
      <Tooltip title={managedServiceInfo}>
        <InfoOutlinedIcon htmlColor={colors.neutral[60]} fontSize='small' />
      </Tooltip>
    </Stack>
  );
}

function useKPITemplatesRedirect() {
  const navigate = useNavigate();
  const templateListLoadable = useAtomValue(kpiActiveTemplatesListStateLoadableJ);

  const isLoading = templateListLoadable.state !== 'hasData';
  const visible = isLoading ? null : (templateListLoadable.data ?? []).filter((t) => !t.isDeleted);

  const handleNavigateToFirstTemplate = useCallback(() => {
    if (!visible || visible.length === 0) {
      return;
    }
    navigate(`/${ROUTES.KPI_TEMPLATES_FULL_ROUTE}/${visible[0].id}`, { replace: true });
  }, [navigate, visible]);

  const handleNavigateNew = useCallback(() => {
    navigate(`/${ROUTES.KPI_TEMPLATES_FULL_ROUTE}/new`);
  }, [navigate]);

  useEffect(() => {
    if (visible && visible.length > 0) {
      handleNavigateToFirstTemplate();
    }
  }, [visible, handleNavigateToFirstTemplate]);

  return { isLoading, hasTemplates: visible !== null && visible.length > 0, handleNavigateNew };
}

export function KPITemplatesRedirect() {
  const { isLoading, hasTemplates, handleNavigateNew } = useKPITemplatesRedirect();

  if (isLoading || hasTemplates) {
    return <Loader />;
  }

  return (
    <EmptySection
      style={{ width: '100%' }}
      title='No Templates Yet'
      message='Create a template to start collecting KPI data from your portfolio companies.'
      actionComponent={
        <Button
          size='medium'
          variant='contained'
          color='secondary'
          startIcon={<AddIcon />}
          onClick={handleNavigateNew}
        >
          Add New Template
        </Button>
      }
    />
  );
}
